import React, {
  useState,
  useMemo,
  useEffect,
  useContext,
  useCallback,
} from "react";
import {
  StyleSheet,
  ScrollView,
  Text,
  TouchableOpacity,
  View as RNView,
  Modal,
  Pressable,
  ActivityIndicator,
  TextInput,
  FlatList,
  Image,
  Platform,
} from "react-native";
import FeatherIcon from "react-native-vector-icons/Feather";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "../../../../utils/useTranslation";
import DeviceOrientationContext from "../../../../components/context/DeviceOrientationContext";
import { View } from "../../../../components/shared/Themed";

// ─── Design Tokens ────────────────────────────────────────────────────────────

const C = {
  navy: "#0F1B2D",
  navyMid: "#1A2E46",
  navyLight: "#243B55",
  accent: "#61B8FF",
  accentDark: "#3A9FE8",
  success: "#0E9F6E",
  successBg: "#EDFAF4",
  warning: "#F59E0B",
  warningBg: "#FFF8EC",
  danger: "#E74C3C",
  dangerBg: "#FEF0EE",
  surface: "#FFFFFF",
  surfaceAlt: "#F7F9FC",
  border: "#E4EAF2",
  borderLight: "#F0F4FA",
  text: "#0F1B2D",
  textMid: "#4A5568",
  textMuted: "#8FA3BF",
};

// ─── Types ────────────────────────────────────────────────────────────────────

interface Product {
  UniqueKey: string;
  ItemName?: string;
  ItemDescription?: string;
  InvntryUom?: string;
  PerCtns?: number;
  PerPallet?: number;
  imageUrl?: string;
  ProductGroup?: string;
}

interface AssignedProduct {
  _id: string;
  uniqueKey: string;
  pricelist?: {
    defaultPrices: { uom: string; price: number }[];
  };
}

type ViewMode = "all" | "assigned" | "unassigned";

const UOMS = ["Pieces", "Boxes", "Pallets"] as const;
type UOM = (typeof UOMS)[number];

// ─── useProductSelection Hook ─────────────────────────────────────────────────

const useProductSelection = ({
  currentProducts,
  assignedProductCodes,
  viewMode,
}: {
  currentProducts: Product[];
  assignedProductCodes: Set<string>;
  viewMode: ViewMode;
}) => {
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(
    new Set(),
  );
  const [selectAll, setSelectAll] = useState(false);
  const [prevViewMode, setPrevViewMode] = useState(viewMode);

  useEffect(() => {
    if (prevViewMode !== viewMode) {
      setSelectedProducts(new Set());
      setPrevViewMode(viewMode);
    }
  }, [viewMode, prevViewMode]);

  useEffect(() => {
    if (
      assignedProductCodes.size > 0 &&
      viewMode === "assigned" &&
      selectedProducts.size === 0
    ) {
      setSelectedProducts(
        new Set(
          currentProducts
            .filter((p) => assignedProductCodes.has(p.UniqueKey))
            .map((p) => p.UniqueKey),
        ),
      );
    }
  }, [currentProducts, assignedProductCodes, viewMode]);

  useEffect(() => {
    const codes = currentProducts.map((p) => p.UniqueKey);
    setSelectAll(
      codes.length > 0 && codes.every((c) => selectedProducts.has(c)),
    );
  }, [currentProducts, selectedProducts]);

  const selectedAssigned = useMemo(
    () =>
      Array.from(selectedProducts).filter((c) => assignedProductCodes.has(c)),
    [selectedProducts, assignedProductCodes],
  );
  const selectedUnassigned = useMemo(
    () =>
      Array.from(selectedProducts).filter((c) => !assignedProductCodes.has(c)),
    [selectedProducts, assignedProductCodes],
  );

  const handleSelectAll = () => {
    const codes = currentProducts.map((p) => p.UniqueKey);
    const updated = new Set(selectedProducts);
    if (selectAll) codes.forEach((c) => updated.delete(c));
    else codes.forEach((c) => updated.add(c));
    setSelectedProducts(updated);
  };

  const handleProductSelect = (code: string) => {
    const updated = new Set(selectedProducts);
    updated.has(code) ? updated.delete(code) : updated.add(code);
    setSelectedProducts(updated);
  };

  const clearSelection = () => setSelectedProducts(new Set());

  return {
    selectedProducts,
    selectAll,
    selectedAssigned,
    selectedUnassigned,
    handleSelectAll,
    handleProductSelect,
    clearSelection,
  };
};

// ─── Pricing Modal ────────────────────────────────────────────────────────────

interface PricingModalProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: (payload: any[]) => void;
  products: Product[];
  organizationId: string;
  loading?: boolean;
  mode?: "assign" | "reassign";
  initialPrices?: Record<string, Partial<Record<UOM, number>>>;
}

const emptyPrices = (): Record<UOM, string> => ({
  Pieces: "",
  Boxes: "",
  Pallets: "",
});

const seedPrices = (
  uniqueKey: string,
  initialPrices?: Record<string, Partial<Record<UOM, number>>>,
): Record<UOM, string> => {
  const existing = initialPrices?.[uniqueKey];
  if (!existing) return emptyPrices();
  return {
    Pieces: existing.Pieces !== undefined ? String(existing.Pieces) : "",
    Boxes: existing.Boxes !== undefined ? String(existing.Boxes) : "",
    Pallets: existing.Pallets !== undefined ? String(existing.Pallets) : "",
  };
};

const PricingModal: React.FC<PricingModalProps> = ({
  visible,
  onClose,
  onConfirm,
  products,
  organizationId,
  loading = false,
  mode = "assign",
  initialPrices,
}) => {
  const isReassign = mode === "reassign";
  const [globalPrices, setGlobalPrices] =
    useState<Record<UOM, string>>(emptyPrices());
  const [productPricing, setProductPricing] = useState<Record<string, any>>({});
  const [validationError, setValidationError] = useState<string | null>(null);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (products.length > 0) {
      setProductPricing(
        Object.fromEntries(
          products.map((p) => [
            p.UniqueKey,
            {
              uniqueKey: p.UniqueKey,
              prices: seedPrices(p.UniqueKey, initialPrices),
              overridden: isReassign,
            },
          ]),
        ),
      );
    }
  }, [products, initialPrices]);

  useEffect(() => {
    if (visible) {
      setGlobalPrices(emptyPrices());
      setValidationError(null);
    }
  }, [visible]);

  const handleGlobalChange = (uom: UOM, value: string) => {
    setGlobalPrices((prev) => ({ ...prev, [uom]: value }));
    setProductPricing((prev) => {
      const next = { ...prev };
      for (const key of Object.keys(next)) {
        if (!next[key].overridden) {
          next[key] = {
            ...next[key],
            prices: { ...next[key].prices, [uom]: value },
          };
        }
      }
      return next;
    });
  };

  const handleProductPriceChange = (
    uniqueKey: string,
    uom: UOM,
    value: string,
  ) => {
    setProductPricing((prev) => {
      const current = prev[uniqueKey];
      const product = products.find((p) => p.UniqueKey === uniqueKey);
      const perCtns = Number(product?.PerCtns ?? 0);
      const perPallet = Number(product?.PerPallet ?? 0);
      const updatedPrices = { ...current.prices, [uom]: value };
      if (uom === "Pieces" && value !== "" && !isNaN(Number(value))) {
        const pieces = Number(value);
        updatedPrices["Boxes"] = perCtns > 0 ? String(pieces * perCtns) : "";
        updatedPrices["Pallets"] =
          perPallet > 0 ? String(pieces * perCtns * perPallet) : "";
      }
      if (uom === "Boxes" && value !== "" && !isNaN(Number(value))) {
        const boxes = Number(value);
        updatedPrices["Pallets"] =
          perPallet > 0 ? String(boxes * perPallet) : "";
      }
      return {
        ...prev,
        [uniqueKey]: { ...current, prices: updatedPrices, overridden: true },
      };
    });
  };

  const handleResetOverride = (uniqueKey: string) => {
    setProductPricing((prev) => ({
      ...prev,
      [uniqueKey]: {
        ...prev[uniqueKey],
        prices: { ...globalPrices },
        overridden: false,
      },
    }));
  };

  const toggleExpand = (key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const handleConfirm = () => {
    for (const product of products) {
      const pricing = productPricing[product.UniqueKey];
      if (!pricing) continue;
      for (const uom of UOMS) {
        const val = pricing.prices[uom];
        if (!val || isNaN(Number(val)) || Number(val) < 0) {
          setValidationError(
            `Missing price for "${product.ItemName || product.UniqueKey}" — ${uom}`,
          );
          return;
        }
      }
    }
    setValidationError(null);
    const payload = products.map((p) => ({
      uniqueKey: p.UniqueKey,
      organizationId,
      defaultPrices: UOMS.map((uom) => ({
        uom,
        price: Number(productPricing[p.UniqueKey].prices[uom]),
      })),
    }));
    onConfirm(payload);
  };

  const overriddenCount = Object.values(productPricing).filter(
    (p: any) => p.overridden,
  ).length;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose}
    >
      <Pressable style={PS.backdrop} onPress={onClose}>
        <Pressable style={PS.sheet} onPress={(e) => e.stopPropagation()}>
          <RNView style={PS.handle} />

          {/* Header */}
          <RNView
            style={[
              PS.header,
              { backgroundColor: isReassign ? C.navyMid : C.navy },
            ]}
          >
            <RNView>
              <Text style={PS.headerTitle}>
                {isReassign ? "Update Pricing" : "Set Pricing"}
              </Text>
              <Text style={PS.headerSub}>
                {products.length} product{products.length !== 1 ? "s" : ""} for{" "}
                {isReassign ? "price update" : "assignment"}
              </Text>
            </RNView>
            <TouchableOpacity style={PS.closeBtn} onPress={onClose}>
              <FeatherIcon name="x" size={18} color="#fff" />
            </TouchableOpacity>
          </RNView>

          <ScrollView style={{ flex: 1 }} showsVerticalScrollIndicator={false}>
            {/* Global pricing */}
            {!isReassign && (
              <RNView style={PS.globalSection}>
                <RNView style={PS.globalTitleRow}>
                  <FeatherIcon name="layers" size={14} color={C.accent} />
                  <Text style={PS.globalTitle}>
                    Bulk Price — applies to all products
                  </Text>
                  {overriddenCount > 0 && (
                    <RNView style={PS.overrideBadge}>
                      <Text style={PS.overrideBadgeText}>
                        {overriddenCount} customised
                      </Text>
                    </RNView>
                  )}
                </RNView>
                <RNView style={PS.uomRow}>
                  {UOMS.map((uom) => (
                    <RNView key={uom} style={PS.uomField}>
                      <Text style={PS.uomLabel}>{uom}</Text>
                      <RNView style={PS.uomInputWrap}>
                        <Text style={PS.uomPrefix}>$</Text>
                        <TextInput
                          style={PS.uomInput}
                          value={globalPrices[uom]}
                          onChangeText={(v) => handleGlobalChange(uom, v)}
                          keyboardType="decimal-pad"
                          placeholder="0.00"
                          placeholderTextColor={C.textMuted}
                        />
                      </RNView>
                    </RNView>
                  ))}
                </RNView>
              </RNView>
            )}

            {/* Validation error */}
            {validationError && (
              <RNView style={PS.errorBanner}>
                <FeatherIcon name="alert-circle" size={14} color={C.danger} />
                <Text style={PS.errorText}>{validationError}</Text>
              </RNView>
            )}

            {/* Per-product list */}
            <RNView style={{ padding: 16 }}>
              <Text style={PS.perProductLabel}>
                {isReassign
                  ? "Pre-filled with existing prices — expand to edit"
                  : "Expand to override per product"}
              </Text>
              {products.map((product) => {
                const pricing = productPricing[product.UniqueKey];
                if (!pricing) return null;
                const isExpanded = expandedKeys.has(product.UniqueKey);
                const perCtns = Number(product.PerCtns ?? 0);
                const perPallet = Number(product.PerPallet ?? 0);

                return (
                  <RNView
                    key={product.UniqueKey}
                    style={[
                      PS.productRow,
                      pricing.overridden && PS.productRowOverridden,
                    ]}
                  >
                    {/* Row header */}
                    <TouchableOpacity
                      style={PS.productRowHeader}
                      onPress={() => toggleExpand(product.UniqueKey)}
                      activeOpacity={0.7}
                    >
                      <RNView style={PS.productThumb}>
                        {product.imageUrl ? (
                          <Image
                            source={{ uri: product.imageUrl }}
                            style={PS.productImg}
                          />
                        ) : (
                          <FeatherIcon
                            name="package"
                            size={18}
                            color={C.accent}
                          />
                        )}
                      </RNView>
                      <RNView style={{ flex: 1 }}>
                        <Text style={PS.productName} numberOfLines={1}>
                          {product.ItemName || product.UniqueKey}
                        </Text>
                        <Text style={PS.productPriceSummary} numberOfLines={1}>
                          {UOMS.map(
                            (uom) => `${uom}: $${pricing.prices[uom] || "—"}`,
                          ).join(" · ")}
                        </Text>
                      </RNView>
                      <RNView
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        {pricing.overridden && (
                          <RNView style={PS.customBadge}>
                            <Text style={PS.customBadgeText}>
                              {isReassign ? "Existing" : "Custom"}
                            </Text>
                          </RNView>
                        )}
                        <FeatherIcon
                          name={isExpanded ? "chevron-up" : "chevron-down"}
                          size={16}
                          color={C.textMuted}
                        />
                      </RNView>
                    </TouchableOpacity>

                    {/* Expanded inputs */}
                    {isExpanded && (
                      <RNView style={PS.expandedBody}>
                        <RNView style={PS.uomRow}>
                          {UOMS.map((uom) => (
                            <RNView key={uom} style={PS.uomField}>
                              <Text style={PS.uomLabel}>{uom}</Text>
                              {(uom === "Boxes" || uom === "Pallets") && (
                                <Text style={PS.uomHelperText}>
                                  {uom === "Boxes"
                                    ? `Pieces × ${perCtns}`
                                    : `Boxes × ${perPallet}`}
                                </Text>
                              )}
                              <RNView style={PS.uomInputWrap}>
                                <Text style={PS.uomPrefix}>$</Text>
                                <TextInput
                                  style={PS.uomInput}
                                  value={pricing.prices[uom]}
                                  onChangeText={(v) =>
                                    handleProductPriceChange(
                                      product.UniqueKey,
                                      uom,
                                      v,
                                    )
                                  }
                                  keyboardType="decimal-pad"
                                  placeholder="0.00"
                                  placeholderTextColor={C.textMuted}
                                />
                              </RNView>
                            </RNView>
                          ))}
                        </RNView>
                        {pricing.overridden && !isReassign && (
                          <TouchableOpacity
                            onPress={() =>
                              handleResetOverride(product.UniqueKey)
                            }
                          >
                            <Text style={PS.resetLink}>
                              Reset to bulk price
                            </Text>
                          </TouchableOpacity>
                        )}
                      </RNView>
                    )}
                  </RNView>
                );
              })}
            </RNView>
          </ScrollView>

          {/* Footer */}
          <RNView style={PS.footer}>
            <TouchableOpacity
              style={PS.cancelBtn}
              onPress={onClose}
              disabled={loading}
            >
              <Text style={PS.cancelBtnText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                PS.confirmBtn,
                { backgroundColor: isReassign ? C.warning : C.accent },
                loading && { opacity: 0.6 },
              ]}
              onPress={handleConfirm}
              disabled={loading}
            >
              {loading ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <Text style={PS.confirmBtnText}>
                  {isReassign
                    ? `Update ${products.length}`
                    : `Assign ${products.length}`}{" "}
                  Product{products.length !== 1 ? "s" : ""}
                </Text>
              )}
            </TouchableOpacity>
          </RNView>
        </Pressable>
      </Pressable>
    </Modal>
  );
};

// ─── Product Card ─────────────────────────────────────────────────────────────

const ProductCard = ({
  product,
  isSelected,
  isAssigned,
  onSelect,
  onAssign,
  onRemove,
  onDetail,
}: {
  product: Product;
  isSelected: boolean;
  isAssigned: boolean;
  onSelect: (key: string) => void;
  onAssign: (key: string) => void;
  onRemove: (key: string) => void;
  onDetail: (key: string) => void;
}) => (
  <TouchableOpacity
    style={[
      PCStyles.card,
      isSelected && PCStyles.cardSelected,
      isAssigned && PCStyles.cardAssigned,
    ]}
    onPress={() => onSelect(product.UniqueKey)}
    activeOpacity={0.85}
  >
    {/* Assigned check */}
    {isAssigned && (
      <RNView style={PCStyles.assignedBadge}>
        <FeatherIcon name="check" size={10} color="#fff" />
      </RNView>
    )}

    {/* Selection checkbox */}
    <RNView
      style={[PCStyles.checkbox, isSelected && PCStyles.checkboxSelected]}
    >
      {isSelected && <FeatherIcon name="check" size={10} color="#fff" />}
    </RNView>

    {/* Image / icon */}
    <RNView style={PCStyles.imgWrap}>
      {product.imageUrl ? (
        <Image source={{ uri: product.imageUrl }} style={PCStyles.img} />
      ) : (
        <RNView style={PCStyles.imgPlaceholder}>
          <FeatherIcon name="package" size={22} color={C.accent} />
        </RNView>
      )}
    </RNView>

    {/* Info */}
    <RNView style={PCStyles.info}>
      <Text style={PCStyles.name} numberOfLines={2}>
        {product.ItemName || product.UniqueKey}
      </Text>
      {!!product.ProductGroup && (
        <RNView style={PCStyles.groupBadge}>
          <Text style={PCStyles.groupText}>{product.ProductGroup}</Text>
        </RNView>
      )}
      <RNView style={PCStyles.metaRow}>
        {!!product.InvntryUom && (
          <Text style={PCStyles.meta}>{product.InvntryUom}</Text>
        )}
        {!!product.PerCtns && (
          <Text style={PCStyles.meta}>· {product.PerCtns} / ctn</Text>
        )}
      </RNView>
    </RNView>

    {/* Actions */}
    <RNView style={PCStyles.actions}>
      <TouchableOpacity
        style={PCStyles.actionBtn}
        onPress={() => onDetail(product.UniqueKey)}
      >
        <FeatherIcon name="info" size={13} color={C.textMuted} />
      </TouchableOpacity>
      {isAssigned ? (
        <TouchableOpacity
          style={[PCStyles.actionBtn, { backgroundColor: C.dangerBg }]}
          onPress={() => onRemove(product.UniqueKey)}
        >
          <FeatherIcon name="minus" size={13} color={C.danger} />
        </TouchableOpacity>
      ) : (
        <TouchableOpacity
          style={[PCStyles.actionBtn, { backgroundColor: C.successBg }]}
          onPress={() => onAssign(product.UniqueKey)}
        >
          <FeatherIcon name="plus" size={13} color={C.success} />
        </TouchableOpacity>
      )}
    </RNView>
  </TouchableOpacity>
);

// ─── Main Screen ──────────────────────────────────────────────────────────────

const CustomerAssignProductsScreen = ({ route, navigation }: any) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const { isLargeDevice, isMediumDevice, isMobileDevice, isSmallDevice } =
    useContext(DeviceOrientationContext);

  // TODO: swap with real redux selectors
  const allProducts: Product[] = useSelector(
    (state: any) => state.admin?.products?.records ?? [],
  );
  const subOrgProducts: AssignedProduct[] = useSelector(
    (state: any) => state.admin?.organizations?.products?.products ?? [],
  );
  const loadingProducts: boolean = useSelector(
    (state: any) => state.admin?.products?.loading ?? false,
  );
  const loadingAction: boolean = useSelector(
    (state: any) =>
      state.admin?.organizations?.products?.loadingAction ?? false,
  );

  const organization = route?.params?.organization;
  const organizationId: string = organization?._id ?? "";
  const pricingEnabled: boolean = organization?.pricingEnabled ?? false;

  // ── Derived data ──────────────────────────────────────────────────────────
  const assignedProductCodes = useMemo(
    () => new Set(subOrgProducts.map((p) => p.uniqueKey)),
    [subOrgProducts],
  );
  const assignedByKey = useMemo(
    () => new Map(subOrgProducts.map((p) => [p.uniqueKey, p])),
    [subOrgProducts],
  );

  // ── UI state ──────────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState<ViewMode>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pricingModalVisible, setPricingModalVisible] = useState(false);
  const [reassignModalVisible, setReassignModalVisible] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [detailProduct, setDetailProduct] = useState<{
    assigned: any;
    sap: any;
  } | null>(null);
  const PAGE_SIZE = 20;

  // ── Filtering ─────────────────────────────────────────────────────────────
  const filteredProducts = useMemo(() => {
    let list = allProducts;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (p) =>
          p.ItemName?.toLowerCase().includes(q) ||
          p.ItemDescription?.toLowerCase().includes(q),
      );
    }
    if (viewMode === "assigned")
      list = list.filter((p) => assignedProductCodes.has(p.UniqueKey));
    if (viewMode === "unassigned")
      list = list.filter((p) => !assignedProductCodes.has(p.UniqueKey));
    return list;
  }, [allProducts, searchQuery, viewMode, assignedProductCodes]);

  const currentProducts = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredProducts.slice(start, start + PAGE_SIZE);
  }, [filteredProducts, currentPage]);

  const totalPages = Math.ceil(filteredProducts.length / PAGE_SIZE);

  // ── Selection ─────────────────────────────────────────────────────────────
  const {
    selectedProducts,
    selectAll,
    selectedAssigned,
    selectedUnassigned,
    handleSelectAll,
    handleProductSelect,
    clearSelection,
  } = useProductSelection({ currentProducts, assignedProductCodes, viewMode });

  // Reset page on search/view change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, viewMode]);

  // ── Handlers (wire to your dispatches) ───────────────────────────────────
  const handleAssignSelected = () => {
    if (selectedUnassigned.length === 0) return;
    if (!pricingEnabled) handleDirectAssign(selectedUnassigned);
    else setPricingModalVisible(true);
  };

  const handleDirectAssign = (uniqueKeys: string[]) => {
    // TODO: dispatch(assignSubOrganizationProductDirect({ payload }))
    console.log("Direct assign:", uniqueKeys);
    clearSelection();
  };

  const handleRemoveSelected = () => {
    if (selectedAssigned.length === 0) return;
    // TODO: dispatch(unAssignSubOrganizationProduct({ payload }))
    console.log("Remove:", selectedAssigned);
    clearSelection();
  };

  const handlePricingConfirm = (payload: any[]) => {
    // TODO: dispatch(assignSubOrganizationProduct({ payload }))
    console.log("Assign with pricing:", payload);
    setPricingModalVisible(false);
    clearSelection();
  };

  const handleReassignConfirm = (payload: any[]) => {
    // TODO: dispatch(reAssignOrganizationProductDirect({ payload }))
    console.log("Reassign pricing:", payload);
    setReassignModalVisible(false);
    clearSelection();
  };

  const handleAssignSingle = (uniqueKey: string) => {
    if (!pricingEnabled) handleDirectAssign([uniqueKey]);
    else {
      handleProductSelect(uniqueKey);
      setPricingModalVisible(true);
    }
  };

  const handleRemoveSingle = (uniqueKey: string) => {
    // TODO: dispatch(unAssignSubOrganizationProduct({ payload: [{ uniqueKey, organizationId }] }))
    console.log("Remove single:", uniqueKey);
  };

  const handleOpenDetail = (uniqueKey: string) => {
    setDetailProduct({
      assigned: assignedByKey.get(uniqueKey) ?? null,
      sap: allProducts.find((p) => p.UniqueKey === uniqueKey) ?? null,
    });
    setDetailVisible(true);
  };

  // ── Counts ────────────────────────────────────────────────────────────────
  const visibleAssignedCount = useMemo(() => {
    const allCodes = new Set(allProducts.map((p) => p.UniqueKey));
    return subOrgProducts.filter((p) => allCodes.has(p.uniqueKey)).length;
  }, [subOrgProducts, allProducts]);

  // initialPrices for reassign
  const initialPricesForReassign = useMemo(() => {
    return Object.fromEntries(
      selectedAssigned.map((code) => {
        const assigned = assignedByKey.get(code);
        const prices: Partial<Record<UOM, number>> = {};
        const uomMap: Record<string, UOM> = {
          BOXES: "Boxes",
          PIECES: "Pieces",
          PALLETS: "Pallets",
        };
        if (assigned?.pricelist?.defaultPrices) {
          for (const dp of assigned.pricelist.defaultPrices) {
            const key = uomMap[dp.uom];
            if (key) prices[key] = dp.price;
          }
        }
        return [code, prices];
      }),
    );
  }, [selectedAssigned, assignedByKey]);

  const isMobile = isSmallDevice || isMobileDevice;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <View style={S.root}>
      {/* ══ HEADER ══════════════════════════════════════════════════════════ */}
      <RNView style={S.header}>
        <RNView style={S.headerTop}>
          <TouchableOpacity
            style={S.backBtn}
            onPress={() => navigation?.goBack()}
            activeOpacity={0.7}
          >
            <FeatherIcon name="arrow-left" size={20} color="#fff" />
          </TouchableOpacity>
          <RNView style={{ flex: 1 }}>
            <Text style={S.headerTitle}>Manage Products</Text>
            <Text style={S.headerSub}>
              {organization?.name ?? "Organisation"}
            </Text>
          </RNView>
          {/* Selection action summary */}
          {selectedProducts.size > 0 && (
            <RNView style={S.selectionBadge}>
              <FeatherIcon name="check-square" size={13} color={C.accent} />
              <Text style={S.selectionBadgeText}>
                {selectedProducts.size} selected
              </Text>
            </RNView>
          )}
        </RNView>

        {/* Bulk action buttons — appear when something is selected */}
        {selectedProducts.size > 0 && (
          <RNView style={S.bulkActions}>
            {selectedUnassigned.length > 0 && (
              <TouchableOpacity
                style={[S.bulkBtn, { backgroundColor: C.accent }]}
                onPress={handleAssignSelected}
                activeOpacity={0.8}
              >
                <FeatherIcon name="shopping-cart" size={13} color="#fff" />
                <Text style={S.bulkBtnText}>
                  Assign ({selectedUnassigned.length})
                </Text>
              </TouchableOpacity>
            )}
            {selectedAssigned.length > 0 && pricingEnabled && (
              <TouchableOpacity
                style={[S.bulkBtn, { backgroundColor: C.warning }]}
                onPress={() => setReassignModalVisible(true)}
                activeOpacity={0.8}
              >
                <FeatherIcon name="refresh-cw" size={13} color="#fff" />
                <Text style={S.bulkBtnText}>
                  Re-price ({selectedAssigned.length})
                </Text>
              </TouchableOpacity>
            )}
            {selectedAssigned.length > 0 && (
              <TouchableOpacity
                style={[S.bulkBtn, { backgroundColor: C.danger }]}
                onPress={handleRemoveSelected}
                activeOpacity={0.8}
              >
                <FeatherIcon name="user-minus" size={13} color="#fff" />
                <Text style={S.bulkBtnText}>
                  Remove ({selectedAssigned.length})
                </Text>
              </TouchableOpacity>
            )}
          </RNView>
        )}
      </RNView>

      {/* ══ CONTROLS BAR ════════════════════════════════════════════════════ */}
      <RNView style={S.controlsBar}>
        {/* Search */}
        <RNView style={S.searchWrap}>
          <FeatherIcon name="search" size={14} color={C.textMuted} />
          <TextInput
            style={S.searchInput}
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholder="Search products…"
            placeholderTextColor={C.textMuted}
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity onPress={() => setSearchQuery("")}>
              <FeatherIcon name="x" size={14} color={C.textMuted} />
            </TouchableOpacity>
          )}
        </RNView>

        {/* View mode tabs */}
        <RNView style={S.viewTabs}>
          {(
            [
              { key: "all", label: "All", count: allProducts.length },
              {
                key: "assigned",
                label: "Assigned",
                count: visibleAssignedCount,
              },
              {
                key: "unassigned",
                label: "Unassigned",
                count: allProducts.length - visibleAssignedCount,
              },
            ] as { key: ViewMode; label: string; count: number }[]
          ).map((tab) => (
            <TouchableOpacity
              key={tab.key}
              style={[S.viewTab, viewMode === tab.key && S.viewTabActive]}
              onPress={() => setViewMode(tab.key)}
              activeOpacity={0.75}
            >
              <Text
                style={[
                  S.viewTabText,
                  viewMode === tab.key && S.viewTabTextActive,
                ]}
              >
                {tab.label}
              </Text>
              <RNView
                style={[
                  S.viewTabCount,
                  viewMode === tab.key && S.viewTabCountActive,
                ]}
              >
                <Text
                  style={[
                    S.viewTabCountText,
                    viewMode === tab.key && S.viewTabCountTextActive,
                  ]}
                >
                  {tab.count}
                </Text>
              </RNView>
            </TouchableOpacity>
          ))}
        </RNView>
      </RNView>

      {/* ══ SELECT ALL ROW ══════════════════════════════════════════════════ */}
      <RNView style={S.selectAllBar}>
        <TouchableOpacity
          style={S.selectAllBtn}
          onPress={handleSelectAll}
          activeOpacity={0.7}
        >
          <RNView style={[S.checkboxLg, selectAll && S.checkboxLgSelected]}>
            {selectAll && <FeatherIcon name="check" size={12} color="#fff" />}
          </RNView>
          <Text style={S.selectAllText}>
            {selectAll
              ? "Deselect all"
              : `Select all ${currentProducts.length}`}
          </Text>
        </TouchableOpacity>
        <Text style={S.resultsCount}>
          {filteredProducts.length} result
          {filteredProducts.length !== 1 ? "s" : ""}
        </Text>
      </RNView>

      {/* ══ PRODUCT LIST ════════════════════════════════════════════════════ */}
      {loadingProducts ? (
        <RNView style={S.loadingWrap}>
          <ActivityIndicator size="large" color={C.accent} />
          <Text style={S.loadingText}>Loading products…</Text>
        </RNView>
      ) : filteredProducts.length === 0 ? (
        <RNView style={S.emptyState}>
          <RNView style={S.emptyIconWrap}>
            <FeatherIcon name="package" size={28} color={C.accent} />
          </RNView>
          <Text style={S.emptyTitle}>No products found</Text>
          <Text style={S.emptySubtitle}>
            {viewMode === "assigned"
              ? "No products assigned yet"
              : viewMode === "unassigned"
                ? "All products have been assigned"
                : "Try adjusting your search"}
          </Text>
        </RNView>
      ) : (
        <FlatList
          data={currentProducts}
          keyExtractor={(item) => item.UniqueKey}
          contentContainerStyle={{ padding: 12, paddingBottom: 24 }}
          showsVerticalScrollIndicator={false}
          renderItem={({ item }) => (
            <ProductCard
              product={item}
              isSelected={selectedProducts.has(item.UniqueKey)}
              isAssigned={assignedProductCodes.has(item.UniqueKey)}
              onSelect={handleProductSelect}
              onAssign={handleAssignSingle}
              onRemove={handleRemoveSingle}
              onDetail={handleOpenDetail}
            />
          )}
          ListFooterComponent={
            totalPages > 1 ? (
              <RNView style={S.pagination}>
                <TouchableOpacity
                  style={[S.pageBtn, currentPage === 1 && S.pageBtnDisabled]}
                  onPress={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <FeatherIcon
                    name="chevron-left"
                    size={16}
                    color={currentPage === 1 ? C.textMuted : C.navy}
                  />
                </TouchableOpacity>

                {/* Page numbers */}
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  const page = i + 1;
                  return (
                    <TouchableOpacity
                      key={page}
                      style={[
                        S.pageBtn,
                        currentPage === page && S.pageBtnActive,
                      ]}
                      onPress={() => setCurrentPage(page)}
                    >
                      <Text
                        style={[
                          S.pageBtnText,
                          currentPage === page && S.pageBtnTextActive,
                        ]}
                      >
                        {page}
                      </Text>
                    </TouchableOpacity>
                  );
                })}

                <TouchableOpacity
                  style={[
                    S.pageBtn,
                    currentPage === totalPages && S.pageBtnDisabled,
                  ]}
                  onPress={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage === totalPages}
                >
                  <FeatherIcon
                    name="chevron-right"
                    size={16}
                    color={currentPage === totalPages ? C.textMuted : C.navy}
                  />
                </TouchableOpacity>
              </RNView>
            ) : null
          }
        />
      )}

      {/* ══ PRICING MODAL (ASSIGN) ══════════════════════════════════════════ */}
      <PricingModal
        visible={pricingModalVisible}
        onClose={() => setPricingModalVisible(false)}
        onConfirm={handlePricingConfirm}
        products={allProducts.filter((p) =>
          selectedUnassigned.includes(p.UniqueKey),
        )}
        organizationId={organizationId}
        loading={loadingAction}
        mode="assign"
      />

      {/* ══ PRICING MODAL (REASSIGN) ════════════════════════════════════════ */}
      <PricingModal
        visible={reassignModalVisible}
        onClose={() => setReassignModalVisible(false)}
        onConfirm={handleReassignConfirm}
        products={allProducts.filter((p) =>
          selectedAssigned.includes(p.UniqueKey),
        )}
        organizationId={organizationId}
        loading={loadingAction}
        mode="reassign"
        initialPrices={initialPricesForReassign}
      />
    </View>
  );
};

export default CustomerAssignProductsScreen;

// ─── Pricing Modal Styles ─────────────────────────────────────────────────────

const PS = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: "rgba(10,20,35,0.55)",
    justifyContent: "flex-end",
  },
  sheet: {
    backgroundColor: C.surface,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    maxHeight: "92%",
    overflow: "hidden",
  },
  handle: {
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: C.border,
    alignSelf: "center",
    marginTop: 10,
    marginBottom: 0,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 20,
    paddingTop: 16,
  },
  headerTitle: { fontSize: 18, fontWeight: "800", color: "#fff" },
  headerSub: { fontSize: 12, color: "rgba(255,255,255,0.7)", marginTop: 2 },
  closeBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: "rgba(255,255,255,0.15)",
    justifyContent: "center",
    alignItems: "center",
  },
  globalSection: {
    backgroundColor: C.surfaceAlt,
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: C.border,
  },
  globalTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginBottom: 12,
  },
  globalTitle: { fontSize: 13, fontWeight: "700", color: C.accent, flex: 1 },
  overrideBadge: {
    backgroundColor: C.warningBg,
    borderRadius: 10,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  overrideBadgeText: { fontSize: 10, fontWeight: "700", color: C.warning },
  uomRow: { flexDirection: "row", gap: 10, flexWrap: "wrap" },
  uomField: { flex: 1, minWidth: 90 },
  uomLabel: {
    fontSize: 11,
    fontWeight: "700",
    color: C.textMid,
    marginBottom: 4,
  },
  uomHelperText: { fontSize: 9, color: C.textMuted, marginBottom: 3 },
  uomInputWrap: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: C.surface,
    borderRadius: 8,
    borderWidth: 1.5,
    borderColor: C.border,
    paddingHorizontal: 8,
    height: 38,
  },
  uomPrefix: { fontSize: 13, color: C.textMuted, marginRight: 4 },
  uomInput: { flex: 1, fontSize: 14, fontWeight: "600", color: C.text },
  errorBanner: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    backgroundColor: C.dangerBg,
    margin: 16,
    padding: 12,
    borderRadius: 8,
  },
  errorText: { fontSize: 12, color: C.danger, flex: 1 },
  perProductLabel: { fontSize: 12, color: C.textMuted, marginBottom: 10 },
  productRow: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: C.border,
    marginBottom: 8,
    backgroundColor: C.surface,
    overflow: "hidden",
  },
  productRowOverridden: { borderColor: C.warning + "60" },
  productRowHeader: {
    flexDirection: "row",
    alignItems: "center",
    padding: 12,
    gap: 10,
  },
  productThumb: {
    width: 36,
    height: 36,
    borderRadius: 8,
    backgroundColor: C.accent + "18",
    justifyContent: "center",
    alignItems: "center",
    flexShrink: 0,
  },
  productImg: { width: 36, height: 36, borderRadius: 8 },
  productName: { fontSize: 13, fontWeight: "700", color: C.text },
  productPriceSummary: { fontSize: 10, color: C.textMuted, marginTop: 2 },
  customBadge: {
    backgroundColor: C.warningBg,
    borderRadius: 8,
    paddingHorizontal: 7,
    paddingVertical: 2,
  },
  customBadgeText: { fontSize: 9, fontWeight: "700", color: C.warning },
  expandedBody: {
    padding: 12,
    paddingTop: 0,
    borderTopWidth: 1,
    borderTopColor: C.borderLight,
  },
  resetLink: {
    fontSize: 12,
    color: C.success,
    fontWeight: "600",
    marginTop: 6,
  },
  footer: {
    flexDirection: "row",
    gap: 10,
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: C.border,
  },
  cancelBtn: {
    flex: 1,
    height: 44,
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: C.border,
    justifyContent: "center",
    alignItems: "center",
  },
  cancelBtnText: { fontSize: 14, fontWeight: "700", color: C.textMid },
  confirmBtn: {
    flex: 2,
    height: 44,
    borderRadius: 10,
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "row",
    gap: 6,
  },
  confirmBtnText: { fontSize: 14, fontWeight: "800", color: "#fff" },
});

// ─── Product Card Styles ──────────────────────────────────────────────────────

const PCStyles = StyleSheet.create({
  card: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: C.surface,
    borderRadius: 12,
    borderWidth: 1.5,
    borderColor: C.borderLight,
    padding: 12,
    marginBottom: 8,
    gap: 10,
    shadowColor: C.navy,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 1,
  },
  cardSelected: { borderColor: C.accent, backgroundColor: "#EBF5FF" },
  cardAssigned: { borderColor: C.success + "50" },
  assignedBadge: {
    position: "absolute",
    top: 8,
    right: 8,
    width: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: C.success,
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 5,
    borderWidth: 2,
    borderColor: C.border,
    justifyContent: "center",
    alignItems: "center",
    flexShrink: 0,
  },
  checkboxSelected: { backgroundColor: C.accent, borderColor: C.accent },
  imgWrap: {
    width: 48,
    height: 48,
    borderRadius: 10,
    overflow: "hidden",
    flexShrink: 0,
  },
  img: { width: 48, height: 48 },
  imgPlaceholder: {
    width: 48,
    height: 48,
    backgroundColor: C.accent + "18",
    justifyContent: "center",
    alignItems: "center",
  },
  info: { flex: 1 },
  name: { fontSize: 13, fontWeight: "700", color: C.text, lineHeight: 18 },
  groupBadge: {
    alignSelf: "flex-start",
    backgroundColor: C.navyLight + "18",
    borderRadius: 6,
    paddingHorizontal: 6,
    paddingVertical: 1,
    marginTop: 3,
  },
  groupText: { fontSize: 9, fontWeight: "700", color: C.navyLight },
  metaRow: { flexDirection: "row", gap: 4, marginTop: 3 },
  meta: { fontSize: 10, color: C.textMuted },
  actions: { flexDirection: "row", gap: 6, flexShrink: 0 },
  actionBtn: {
    width: 30,
    height: 30,
    borderRadius: 8,
    backgroundColor: C.surfaceAlt,
    justifyContent: "center",
    alignItems: "center",
  },
});

// ─── Screen Styles ────────────────────────────────────────────────────────────

const S = StyleSheet.create({
  root: { flex: 1, backgroundColor: C.surfaceAlt },

  // Header
  header: {
    backgroundColor: C.navy,
    paddingTop: Platform.OS === "ios" ? 52 : 16,
    paddingHorizontal: 16,
    paddingBottom: 14,
    shadowColor: C.navy,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 12,
    elevation: 8,
  },
  headerTop: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    marginBottom: 0,
  },
  backBtn: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: C.navyLight,
    justifyContent: "center",
    alignItems: "center",
  },
  headerTitle: { fontSize: 18, fontWeight: "800", color: "#fff" },
  headerSub: { fontSize: 12, color: C.textMuted, marginTop: 1 },
  selectionBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
    backgroundColor: C.accent + "20",
    borderRadius: 10,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderWidth: 1,
    borderColor: C.accent + "40",
  },
  selectionBadgeText: { fontSize: 12, fontWeight: "700", color: C.accent },
  bulkActions: {
    flexDirection: "row",
    gap: 8,
    marginTop: 12,
    flexWrap: "wrap",
  },
  bulkBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
    paddingVertical: 7,
    paddingHorizontal: 14,
    borderRadius: 20,
  },
  bulkBtnText: { fontSize: 12, fontWeight: "700", color: "#fff" },

  // Controls
  controlsBar: {
    backgroundColor: C.surface,
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: C.border,
    gap: 10,
  },
  searchWrap: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    backgroundColor: C.surfaceAlt,
    borderRadius: 10,
    paddingHorizontal: 12,
    height: 40,
    borderWidth: 1.5,
    borderColor: C.border,
  },
  searchInput: { flex: 1, fontSize: 14, color: C.text },
  viewTabs: { flexDirection: "row", gap: 6 },
  viewTab: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 6,
    paddingHorizontal: 14,
    borderRadius: 20,
    backgroundColor: C.surfaceAlt,
    borderWidth: 1.5,
    borderColor: C.border,
    gap: 6,
  },
  viewTabActive: { backgroundColor: C.navy, borderColor: C.navy },
  viewTabText: { fontSize: 12, fontWeight: "600", color: C.textMid },
  viewTabTextActive: { color: "#fff" },
  viewTabCount: {
    backgroundColor: C.border,
    borderRadius: 10,
    paddingHorizontal: 6,
    paddingVertical: 1,
  },
  viewTabCountActive: { backgroundColor: "rgba(255,255,255,0.2)" },
  viewTabCountText: { fontSize: 10, fontWeight: "700", color: C.textMid },
  viewTabCountTextActive: { color: "#fff" },

  // Select all
  selectAllBar: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: C.surface,
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: C.borderLight,
  },
  selectAllBtn: { flexDirection: "row", alignItems: "center", gap: 8 },
  checkboxLg: {
    width: 22,
    height: 22,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: C.border,
    justifyContent: "center",
    alignItems: "center",
  },
  checkboxLgSelected: { backgroundColor: C.navy, borderColor: C.navy },
  selectAllText: { fontSize: 13, fontWeight: "600", color: C.textMid },
  resultsCount: { fontSize: 12, color: C.textMuted },

  // Loading / empty
  loadingWrap: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingTop: 60,
    gap: 12,
  },
  loadingText: { fontSize: 14, color: C.textMuted },
  emptyState: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingTop: 80,
    gap: 10,
  },
  emptyIconWrap: {
    width: 64,
    height: 64,
    borderRadius: 20,
    backgroundColor: C.accent + "18",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 4,
  },
  emptyTitle: { fontSize: 17, fontWeight: "800", color: C.text },
  emptySubtitle: { fontSize: 13, color: C.textMuted },

  // Pagination
  pagination: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: 6,
    paddingTop: 16,
  },
  pageBtn: {
    width: 36,
    height: 36,
    borderRadius: 8,
    backgroundColor: C.surface,
    borderWidth: 1.5,
    borderColor: C.border,
    justifyContent: "center",
    alignItems: "center",
  },
  pageBtnActive: { backgroundColor: C.navy, borderColor: C.navy },
  pageBtnDisabled: { opacity: 0.35 },
  pageBtnText: { fontSize: 13, fontWeight: "700", color: C.text },
  pageBtnTextActive: { color: "#fff" },
});
